import OneSignal from 'react-onesignal';

// Convert a base64url-encoded VAPID public key string to a Uint8Array,
// the shape PushManager.subscribe()'s applicationServerKey expects per
// the Web Push spec. The string form works in mainstream browsers as a
// Chromium-specific convenience, but spec-conformant code must pass a
// BufferSource; this keeps the diagnostic in forceSubscribeIfMissing
// working uniformly across older Chromium forks and embedded WebViews.
//
// We construct the backing ArrayBuffer explicitly (rather than letting
// Uint8Array allocate its own) so the runtime instance is genuinely
// ArrayBuffer-backed (never SharedArrayBuffer-backed). The call site
// casts the result to BufferSource — TS 5.7+ widened Uint8Array's
// internal generic so the default `Uint8Array` annotation isn't
// directly assignable to BufferSource, but the underlying bytes are
// always a valid BufferSource at runtime.
function urlBase64ToUint8Array(base64Url: string): Uint8Array {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const buffer = new ArrayBuffer(raw.length);
    const out = new Uint8Array(buffer);
    for (let i = 0; i < raw.length; i++) {
        out[i] = raw.charCodeAt(i);
    }
    return out;
}

/**
 * OneSignal Push Notification Service
 *
 * Handles OneSignal SDK initialization, user login/logout,
 * and browser permission management.
 *
 * Flow:
 * 1. App loads → init(app_id) with tenant's OneSignal app ID
 * 2. User logs in → login(user_id) links this device to the user
 * 3. User enables push → requestPermission() triggers browser prompt
 * 4. Backend sends notification targeting external_id (user_id)
 * 5. OneSignal delivers to ALL devices where user called login()
 * 6. User logs out → logout() unlinks the device
 */

class OneSignalService {
    private is_initialized = false;
    // Cached in-flight init promise. Concurrent callers (e.g. React.StrictMode
    // double-mount) all await the same promise instead of each running their
    // own OneSignal.init(), which would otherwise double-register the
    // subscription and trigger TWO welcome pushes from OneSignal.
    private init_promise: Promise<boolean> | null = null;
    // Tracks the most recent user_id passed to login() so re-calls with the
    // same id are a no-op. Without this, login() running twice would tell
    // OneSignal to re-link the subscription and the welcome push fires again.
    private last_logged_in_user_id: string | null = null;
    // Tracks whether the slidedown was opened by our own showSubscribePrompt()
    // call. Anything else triggering a slidedown (dashboard auto-config, SDK
    // internal heuristics, etc.) is silently dismissed by the guard registered
    // in init(). Window remains open ~1.5s after promptPush() to cover SDK
    // animation delay before the slidedownShown event fires.
    private is_prompting_explicitly = false;
    // Tenant's Collapsed Menu Logo from Business Settings, captured at init
    // and injected into the slidedown DOM on every render. OneSignal v16
    // ignores per-prompt `icon` config, so we have to swap the <img> ourselves.
    private slidedown_icon_url?: string;
    // Which button the user clicked on the most recent slidedown.
    // Reset on every showSubscribePrompt() call. Lets the caller distinguish
    // "Enable clicked → native prompt next, save ON" from "Not now / dismiss
    // → save OFF, fire toast".
    private slidedown_user_action: 'enabled' | 'cancelled' | null = null;

    /**
     * Initialize OneSignal SDK with the tenant's app ID.
     * Should be called once when the app loads and tenant config is available.
     * `safari_web_id` is optional and silently ignored on non-Safari browsers.
     * Required for macOS Safari push subscription (paired with the tenant's
     * Apple Push Authentication Key uploaded in OneSignal dashboard).
     * `slidedown` overrides the slidedown prompt content (icon + message)
     * with the tenant's company name and Collapsed Menu Logo from Business
     * Settings so the prompt matches the rest of the admin/learner UI.
     */
    async init(
        app_id: string,
        safari_web_id?: string,
        slidedown?: { actionMessage?: string; icon?: string }
    ): Promise<boolean> {
        // Already done — return immediately.
        if (this.is_initialized) {
            return true;
        }
        // Init in flight from a previous concurrent caller — both wait on
        // the same promise so only one OneSignal.init() actually runs.
        if (this.init_promise) {
            return this.init_promise;
        }

        // Capture the icon BEFORE creating the promise — needed by
        // injectSlidedownLogo via the slidedownShown listener.
        this.slidedown_icon_url = slidedown?.icon;

        if (!app_id) {
            console.warn('[OneSignal] No app_id provided, skipping init');
            return false;
        }

        this.init_promise = this._doInit(app_id, safari_web_id, slidedown);
        try {
            return await this.init_promise;
        } finally {
            // Keep init_promise set forever once initialized — that way later
            // callers hit the `is_initialized` early return above. Only clear
            // it if init failed so a retry is possible.
            if (!this.is_initialized) {
                this.init_promise = null;
            }
        }
    }

    private async _doInit(
        app_id: string,
        safari_web_id?: string,
        slidedown?: { actionMessage?: string; icon?: string }
    ): Promise<boolean> {
        try {
            // Build the slidedown prompt config. autoPrompt:false keeps our
            // manual-only flow (only fires from showSubscribePrompt()).
            // The text + icon make the real slidedown match the Skillzen
            // branding instead of OneSignal's stock copy.
            // `any` because react-onesignal's strict type doesn't expose the
            // `icon` field even though OneSignal v16 SDK accepts it.
            const slidedownPrompt: any = {
                type: 'push',
                autoPrompt: false,
                delay: { pageViews: 0, timeDelay: 0 },
                text: {
                    actionMessage:
                        slidedown?.actionMessage ||
                        'Stay updated with notifications from this app.',
                    acceptButton: 'Enable',
                    cancelButton: 'Not now',
                },
            };
            if (slidedown?.icon) {
                slidedownPrompt.icon = slidedown.icon;
            }

            await OneSignal.init({
                appId: app_id,
                ...(safari_web_id ? { safari_web_id } : {}),
                allowLocalhostAsSecureOrigin: import.meta.env.DEV,
                serviceWorkerParam: { scope: '/' },
                serviceWorkerPath: 'OneSignalSDKWorker.js',
                notifyButton: {
                    enable: false,
                    prenotify: false,
                    showCredit: false,
                    text: {} as any,
                },
                // Manual-only flow: prompt is shown from showSubscribePrompt()
                // in handleSave, never auto-fired by OneSignal heuristics.
                promptOptions: {
                    slidedown: {
                        prompts: [slidedownPrompt],
                    },
                },
            });

            this.is_initialized = true;
            console.log('[OneSignal] Initialized successfully');

            // Slidedown kill-switch + icon injection.
            // Kill-switch: anything that opens a slidedown without our
            // explicit flag (dashboard auto-prompt, SDK heuristics, etc.)
            // is dismissed immediately. Save-click flow sets the flag in
            // showSubscribePrompt() so its slidedown survives.
            // Icon injection: when it IS our prompt, swap the OneSignal
            // bell <img> for the tenant's Collapsed Menu Logo because
            // OneSignal v16 doesn't honor `icon` in promptOptions config.
            try {
                (OneSignal as any).Slidedown.addEventListener('slidedownShown', () => {
                    if (this.is_prompting_explicitly) {
                        this.injectSlidedownLogo();
                        return;
                    }
                    this.dismissActiveSlidedown();
                });
            } catch (error) {
                console.warn('[OneSignal] Could not attach slidedownShown guard', error);
            }

            return true;
        } catch (error: any) {
            if (error?.message?.includes('already initialized')) {
                this.is_initialized = true;
                return true;
            }
            console.error('[OneSignal] Init failed:', error);
            return false;
        }
    }

    /**
     * Login user to OneSignal using their user_id as external_id.
     * This links the current browser/device to the user.
     * Must be called after init() and after user authentication.
     */
    async login(user_id: string): Promise<boolean> {
        if (!this.is_initialized) {
            console.warn('[OneSignal] Not initialized, skipping login');
            return false;
        }

        if (!user_id) {
            console.warn('[OneSignal] No user_id provided, skipping login');
            return false;
        }

        // Already logged in as this user — re-calling OneSignal.login() with
        // the same external_id can re-trigger the welcome notification, so
        // skip if nothing changed (e.g. React.StrictMode re-running effects).
        if (this.last_logged_in_user_id === user_id) {
            return true;
        }

        try {
            await OneSignal.login(user_id);
            this.last_logged_in_user_id = user_id;
            console.log('[OneSignal] User logged in:', user_id);

            // Log subscription state after login for debugging
            this.logSubscriptionState();

            return true;
        } catch (error) {
            console.error('[OneSignal] Login failed:', error);
            return false;
        }
    }

    /**
     * Logout user from OneSignal.
     * Unlinks the current device from the user's external_id.
     * Must be called when user logs out of the app.
     */
    async logout(): Promise<void> {
        if (!this.is_initialized) {
            return;
        }

        try {
            await OneSignal.logout();
            this.last_logged_in_user_id = null;
            console.log('[OneSignal] User logged out');
        } catch (error) {
            console.error('[OneSignal] Logout failed:', error);
        }
    }

    /**
     * Request browser push notification permission via the native prompt only.
     * Does NOT auto opt-in after grant — the caller must explicitly call
     * optIn() or showSubscribePrompt() if they want to subscribe.
     * Retained as a low-level API; the app's default flow uses showSubscribePrompt().
     */
    async requestPermission(): Promise<boolean> {
        if (!this.is_initialized) {
            console.warn('[OneSignal] Not initialized, cannot request permission');
            return false;
        }

        try {
            await OneSignal.Notifications.requestPermission();
            const granted = OneSignal.Notifications.permission;
            console.log('[OneSignal] Permission result:', granted);
            return granted;
        } catch (error) {
            console.error('[OneSignal] Permission request failed:', error);
            return false;
        }
    }

    /**
     * Whether the current browser session is currently opted into push
     * (has an active OneSignal subscription).
     */
    isOptedIn(): boolean {
        if (!this.is_initialized) return false;
        try {
            return OneSignal.User.PushSubscription.optedIn === true;
        } catch {
            return false;
        }
    }

    /**
     * Show OneSignal's slidedown prompt asking the user to subscribe.
     * force:true bypasses OneSignal's internal suppression rules so the
     * prompt always shows, even for users who previously dismissed it.
     * User must click Subscribe for the opt-in to happen — no auto-subscribe.
     */
    async showSubscribePrompt(): Promise<void> {
        if (!this.is_initialized) {
            console.warn('[OneSignal] Not initialized, cannot show subscribe prompt');
            return;
        }
        try {
            // Reset the per-prompt user action so callers don't see a stale
            // value from the previous slidedown.
            this.slidedown_user_action = null;
            // Mark this slidedown as ours so the slidedownShown guard in init()
            // doesn't auto-dismiss it. Released after a short window covering
            // SDK animation + event dispatch delay.
            this.is_prompting_explicitly = true;
            setTimeout(() => { this.is_prompting_explicitly = false; }, 1500);
            await OneSignal.Slidedown.promptPush({ force: true });
        } catch (error) {
            console.warn('[OneSignal] Slidedown prompt failed', error);
        }
    }

    /**
     * Returns which button the user clicked on the most recent slidedown.
     *   'enabled'   → user clicked Enable; native browser prompt is opening
     *   'cancelled' → user clicked Not now
     *   null        → no button click yet, OR slidedown was dismissed via
     *                 the X / Escape / click-outside (treat as cancelled)
     */
    getSlidedownUserAction(): 'enabled' | 'cancelled' | null {
        return this.slidedown_user_action;
    }

    /**
     * Public wrapper to forcefully hide whatever slidedown is currently
     * visible. Useful when our flow times out waiting for the user to
     * subscribe — the slidedown should not linger in the UI past the
     * "Push notifications not enabled" toast.
     */
    hideSlidedown(): void {
        this.dismissActiveSlidedown();
    }

    /**
     * Dismiss whatever slidedown is currently visible. Used by the kill-switch
     * in init() to suppress auto-fired prompts. Tries the SDK's cancel button
     * first (so OneSignal cleans up its own state), then falls back to a hard
     * DOM remove if the button isn't found.
     */
    private dismissActiveSlidedown(): void {
        try {
            const cancel_btn = document.querySelector(
                '#onesignal-slidedown-cancel-button, .onesignal-slidedown-cancel-button'
            ) as HTMLElement | null;
            if (cancel_btn) {
                cancel_btn.click();
                return;
            }
            const container = document.getElementById('onesignal-slidedown-container');
            if (container) container.remove();
        } catch (error) {
            console.warn('[OneSignal] Auto-dismiss of unwanted slidedown failed', error);
        }
    }

    /**
     * Once the slidedown is in the DOM:
     *   1. Replace OneSignal's default bell with the tenant's Collapsed Menu
     *      Logo (OneSignal v16 ignores the `icon` field in promptOptions
     *      config, so we DOM-swap after render).
     *   2. Attach click listeners on Enable / Not now so the caller can
     *      tell why the slidedown disappeared (Enable click vs dismiss).
     */
    private injectSlidedownLogo(): void {
        let attempts = 0;
        const tryInject = () => {
            attempts++;
            const iconEl = document.querySelector(
                '#onesignal-slidedown-container .slidedown-body-icon'
            ) as HTMLElement | null;
            if (!iconEl) {
                if (attempts < 10) setTimeout(tryInject, 50);
                return;
            }
            // Track button clicks. Use capture phase so we get the click
            // event before OneSignal's own handler removes the slidedown.
            const allowBtn = document.getElementById('onesignal-slidedown-allow-button');
            const cancelBtn = document.getElementById('onesignal-slidedown-cancel-button');
            allowBtn?.addEventListener(
                'click',
                () => { this.slidedown_user_action = 'enabled'; },
                { once: true, capture: true }
            );
            cancelBtn?.addEventListener(
                'click',
                () => { this.slidedown_user_action = 'cancelled'; },
                { once: true, capture: true }
            );
            // Swap in the company logo when configured (otherwise leave the
            // OneSignal default bell with our violet-tint CSS theme).
            if (!this.slidedown_icon_url) return;
            const url = this.slidedown_icon_url;
            iconEl.classList.add('has-logo');
            iconEl.innerHTML = `<img src="${url}" alt="" />`;
        };
        tryInject();
    }

    /**
     * Ensure user is opted into push subscription.
     * Handles cases where subscription was lost due to SW re-registration.
     */
    async ensureSubscription(): Promise<void> {
        try {
            const optedIn = OneSignal.User.PushSubscription.optedIn;
            console.log('[OneSignal] Push subscription optedIn:', optedIn);

            if (!optedIn) {
                console.log('[OneSignal] Re-opting in to push subscription...');
                await OneSignal.User.PushSubscription.optIn();
                console.log('[OneSignal] Re-opted in successfully');
            }

            this.logSubscriptionState();
        } catch (error) {
            console.error('[OneSignal] ensureSubscription failed:', error);
        }
    }

    /**
     * Log current subscription state for debugging.
     */
    logSubscriptionState(): void {
        try {
            const subId = OneSignal.User.PushSubscription.id;
            const token = OneSignal.User.PushSubscription.token;
            const optedIn = OneSignal.User.PushSubscription.optedIn;
            const externalId = OneSignal.User.externalId;
            const onesignalId = OneSignal.User.onesignalId;
            const permission = OneSignal.Notifications.permission;

            console.log('[OneSignal] Subscription state:', {
                subscription_id: subId || 'none',
                token: token ? `${token.substring(0, 20)}...` : 'none',
                opted_in: optedIn,
                external_id: externalId || 'none',
                onesignal_id: onesignalId || 'none',
                permission,
            });
        } catch (error) {
            console.warn('[OneSignal] Could not read subscription state:', error);
        }
    }

    /**
     * Check if push notifications are currently permitted by the browser.
     */
    getPermissionStatus(): boolean {
        if (!this.is_initialized) {
            return false;
        }

        return OneSignal.Notifications.permission;
    }

    /**
     * Check if OneSignal SDK has been initialized.
     */
    isReady(): boolean {
        return this.is_initialized;
    }

    /**
     * Returns the current OneSignal push token (FCM/WNS endpoint URL) if any.
     * Empty / null / undefined indicates that the browser-level
     * PushManager.subscribe() either failed or has not been attempted.
     */
    getPushToken(): string | null {
        if (!this.is_initialized) return null;
        try {
            const token = OneSignal.User.PushSubscription.token;
            return token && typeof token === 'string' ? token : null;
        } catch {
            return null;
        }
    }

    /**
     * Detects the "push is silently blocked by the browser" state.
     *
     * Returns true ONLY when all of the following hold:
     *   - SDK is initialized
     *   - Browser notification permission is 'granted' (user said yes)
     *   - OneSignal reports the user as opted-in
     *   - But no push token exists
     *
     * This combination is impossible under normal operation — it only happens
     * when the browser's push service refused the subscription registration.
     * In practice, the #1 cause is Microsoft Edge's Tracking Prevention
     * blocking OneSignal as a tracker on the current origin. Other causes:
     * InPrivate / incognito windows, Windows Focus Assist + notifications
     * disabled at OS level, corporate group policy blocking push.
     */
    isPushBlockedByBrowser(): boolean {
        if (!this.is_initialized) return false;
        if (typeof Notification === 'undefined') return false;
        if (Notification.permission !== 'granted') return false;
        if (!this.isOptedIn()) return false;
        return !this.getPushToken();
    }

    /**
     * Force a fresh PushManager.subscribe() when we detect that permission
     * is granted but the browser has no underlying push subscription.
     *
     * This is the Edge-survives-without-cookies-clear path: even after our
     * stale-state recovery (logout + SW unregister + IDB wipe + reload), the
     * SDK may finish init without re-creating a browser-level subscription
     * — typically because Tracking Prevention or some other env factor blocks
     * OneSignal's auto-subscribe. We then ask the SDK to opt in explicitly.
     * If that ALSO fails (Edge refuses), the error gets logged with name +
     * message so we can pinpoint the real cause without manual console work.
     *
     * Safe to call any time after init() resolves. No-op when the browser
     * already has a subscription, or when permission isn't granted (in which
     * case the user must use the slidedown flow).
     *
     * Returns true if a subscription exists at the browser level after the
     * attempt (either was already there, or our optIn succeeded).
     */
    async forceSubscribeIfMissing(): Promise<boolean> {
        if (!this.is_initialized) return false;
        if (typeof Notification === 'undefined') return false;
        if (Notification.permission !== 'granted') return false;

        try {
            const reg = await navigator.serviceWorker.getRegistration();
            const existing = await reg?.pushManager?.getSubscription();
            if (existing) {
                return true;
            }

            console.warn(
                '[OneSignal] Permission granted but no browser-level push subscription — forcing optIn()'
            );
            await OneSignal.User.PushSubscription.optIn();

            // Re-check. SDK opt-in is async on the wire; give the SW a
            // moment to surface the new subscription before we report.
            await new Promise((r) => setTimeout(r, 500));
            const after = await reg?.pushManager?.getSubscription();
            if (after) {
                console.log('[OneSignal] forceSubscribeIfMissing succeeded — endpoint:', after.endpoint);
                this.logSubscriptionState();
                return true;
            }

            console.error(
                '[OneSignal] forceSubscribeIfMissing: optIn() completed but browser still has no subscription. ' +
                    'The browser-level PushManager.subscribe() is being silently rejected. ' +
                    'Most likely causes: Edge Tracking Prevention, InPrivate window, Windows Focus Assist, ' +
                    'corporate group policy, or AV blocking push service.'
            );

            // Final diagnostic: try the raw browser API directly so we get the
            // actual rejection reason. OneSignal's optIn() swallows the
            // underlying PushManager error. This call uses a public test VAPID
            // key (from Google's web-push samples) — we don't care about the
            // resulting subscription (we clean it up if it succeeds), only
            // about the error the browser surfaces.
            //
            // applicationServerKey must be a BufferSource per the Web Push
            // spec. Chrome/Firefox accept the raw base64url string as a
            // Chromium-specific convenience, but older Chromium forks and
            // some embedded WebViews reject it with a TypeError before the
            // real subscribe even runs — which would mask the actual
            // platform error we're trying to extract. Convert to Uint8Array
            // so the call is spec-conformant and works everywhere.
            try {
                console.warn(
                    '[OneSignal] Running raw pushManager.subscribe() diagnostic to extract real error...'
                );
                const test_vapid_key_b64 =
                    'BIgRRnnsBLihLA9PsK2BgYz5GtA9oJVuxsR4O4lA6mWPMvENRjMy7w2hKkpJgvqkbB0HrxLLkRq60yA1q-jUI2A';
                const raw_sub = await reg!.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(test_vapid_key_b64) as BufferSource,
                });
                console.warn(
                    '[OneSignal] Raw subscribe SUCCEEDED. Endpoint:',
                    raw_sub.endpoint
                );
                console.warn(
                    '[OneSignal] => Browser is willing to subscribe. OneSignal SDK is the blocker (likely VAPID fetch / internal flow). ' +
                        'Cleaning up test subscription.'
                );
                await raw_sub.unsubscribe();
            } catch (raw_err: any) {
                console.error(
                    '[OneSignal] Raw subscribe FAILED — actual browser rejection:',
                    {
                        name: raw_err?.name,
                        message: raw_err?.message,
                    }
                );
                console.error(
                    '[OneSignal] => This error is the root cause. Edge/Windows/network is blocking PushManager.subscribe() at the platform level — no JavaScript can override this.'
                );
            }
            return false;
        } catch (error: any) {
            console.error(
                '[OneSignal] forceSubscribeIfMissing threw:',
                {
                    name: error?.name,
                    message: error?.message,
                    error,
                }
            );
            return false;
        }
    }

    /**
     * Register a listener for notification click events.
     * Used to handle navigation when user clicks a push notification.
     */
    onNotificationClick(handler: (event: any) => void): void {
        if (!this.is_initialized) {
            return;
        }

        OneSignal.Notifications.addEventListener('click', handler);
    }

    /**
     * Register a listener for permission changes.
     */
    onPermissionChange(handler: (permission: boolean) => void): void {
        if (!this.is_initialized) {
            return;
        }

        OneSignal.Notifications.addEventListener('permissionChange', handler);
    }

    /**
     * Register a listener for foreground notifications.
     * Fires when a notification arrives while the app is open/focused.
     * Must call event.notification.display() to show the notification.
     */
    onForegroundWillDisplay(handler: (event: any) => void): void {
        if (!this.is_initialized) {
            return;
        }

        OneSignal.Notifications.addEventListener('foregroundWillDisplay', handler);
    }

    /**
     * Register a listener for push subscription changes.
     * Useful for debugging when the subscription token changes or is lost.
     */
    onSubscriptionChange(handler: (change: any) => void): void {
        if (!this.is_initialized) {
            return;
        }

        OneSignal.User.PushSubscription.addEventListener('change', handler);
    }
}

export const oneSignalService = new OneSignalService();
